import React, { useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, BrainCircuitIcon, ImagesIcon, SparklesIcon } from 'lucide-react';
import { ImageData } from '../utils/mockData';
import { motion, useAnimation, useInView } from 'framer-motion';
interface AssetAnalysisProps {
  data: ImageData[];
}
export const AssetAnalysis: React.FC<AssetAnalysisProps> = ({
  data
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isNextHovered, setIsNextHovered] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [sliderPosition, setSliderPosition] = useState(50); // 0-100 percentage
  const [isDragging, setIsDragging] = useState(false);
  const [exporting, setExporting] = useState(false);
  // While the PDF is being generated, switch from the interactive single-asset
  // carousel to a static block that shows all assets with full-opacity text.
  useEffect(() => {
    const start = () => setExporting(true);
    const end = () => setExporting(false);
    window.addEventListener('report:export-start', start);
    window.addEventListener('report:export-end', end);
    return () => {
      window.removeEventListener('report:export-start', start);
      window.removeEventListener('report:export-end', end);
    };
  }, []);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const insightsRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(insightsRef, {
    once: true,
    amount: 0.3
  });
  const controls = useAnimation();
  const selectedAsset = data[selectedIndex];
  useEffect(() => {
    if (isInView) {
      controls.start({
        x: 0,
        opacity: 1,
        transition: {
          duration: 0.8,
          ease: 'easeOut'
        }
      });
    }
  }, [isInView, controls]);
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStart(touch.clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const touch = e.touches[0];
    const currentTouch = touch.clientX;
    const diff = touchStart - currentTouch;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextAsset();
      } else {
        prevAsset();
      }
      setTouchStart(null);
    }
  };
  // Slider drag handlers
  const handleSliderMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    updateSliderPosition(e.clientX);
  };
  const handleSliderTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    updateSliderPosition(e.touches[0].clientX);
  };
  const updateSliderPosition = (clientX: number) => {
    if (!videoContainerRef.current) return;
    const rect = videoContainerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, x / rect.width * 100));
    setSliderPosition(percentage);
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        updateSliderPosition(e.clientX);
      }
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging) {
        updateSliderPosition(e.touches[0].clientX);
      }
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);
  const getAIFeedback = (asset: ImageData) => {
    switch (asset.id) {
      case 1:
        return {
          summary: 'What works (and why):',
          details: [<span key="1" data-id="element-8" data-mp="d79n5d4swacg">
              This asset acts as a strong pattern interrupt in the busy travel
              retail space. Warm colours, particularly red and yellow, are the
              most attention-grabbing. The distinct red background ensures the
              message breaks through the visual noise because any colour stands
              out against its background through contrast and difference from
              the average. Furthermore, red primarily draws attention when
              paired with emotional content or high-arousal environments.
            </span>, <span key="2" data-id="element-9" data-mp="2zg8snh645c4z">
              The large typography also acts as an immediate visual magnet. Text
              is an attention-capturing stimulus that rivals faces in its
              ability to attract gaze. In over 65% of trials, text is foveated
              within the first two fixations.
            </span>]
        };
      case 2:
        return {
          summary: 'What works (and why):',
          details: [<span key="1" data-id="element-10" data-mp="14uw74mpck3re">
              This variant relies on immediate visibility and strong luminance
              contrast. Yellow is the most visible and fastest-noticed colour
              overall. The clear difference between the black typography and the
              bright yellow background leverages this effectively. Increasing an
              object's luminance contrast relative to its background increases
              both fixations and detection.
            </span>, <span key="2" data-id="element-11" data-mp="1srjmm97ch60g">
              The design also relies on surface size to guide the eye, as larger
              objects and text are more likely to attract attention.
            </span>]
        };
      case 3:
        return {
          summary: 'What does not work? (why it underperforms):',
          details: [<span key="1" data-id="element-12" data-mp="26l19o3z8p0v8">
              While the physical size of the red text efficiently guides
              attention, the overall design lacks the stopping power needed for
              this specific environment. The white background blends into the
              ambient lighting, the white ceiling, and the light-coloured
              architecture of the store mockup.
            </span>, <span key="2" data-id="element-13" data-mp="l7pg5vsc8p12">
              Colour contrast works by making objects with distinctive colours
              stand out from their surroundings. Without a strong background
              colour to create a clear visual boundary, this asset fails to
              capture the passive attention of a moving traveller.
            </span>]
        };
      case 4:
        return {
          summary: 'What does not work? (why it underperforms):',
          details: [<span key="1" data-id="element-14" data-mp="z7798nco2og8">
              Designed for a premium aesthetic, this asset suffers from visual
              camouflage. The dark background offers almost zero contrast when
              placed on the dark grey digital pillars in the store. In everyday
              environments, local colour contrasts are typically associated with
              interesting and relevant objects that can grab attention in a
              stimulus-driven manner.
            </span>, <span key="2" data-id="element-15" data-mp="16q6smt04ofzl">
              Because this variant blends in with the physical pillar, it lacks
              the necessary visual discrepancy to stand out. Coloured content
              attracts substantially more attention compared to black and white
              alternatives, meaning this dark variant is easily overlooked
              during a quick visual scan.
            </span>]
        };
      default:
        return {
          summary: 'Analysis not available',
          details: []
        };
    }
  };
  const nextAsset = () => {
    setSelectedIndex(prev => (prev + 1) % data.length);
  };
  const prevAsset = () => {
    setSelectedIndex(prev => (prev - 1 + data.length) % data.length);
  };
  const getNextAssetName = () => {
    const nextIndex = (selectedIndex + 1) % data.length;
    return data[nextIndex].name;
  };
  const getProbability = (id: number) => id === 1 ? '88.5' : id === 2 ? '11.2' : id === 3 ? '0.2' : '0.1';
  const feedback = getAIFeedback(selectedAsset);
  return <div className="space-y-6" data-id="element-16" data-mp="112xggx2o3ut7">
      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-17" data-mp="1125l9nczfz3g">
        <div className="flex items-start space-x-3" data-id="element-18" data-mp="r6cc8dyh2nqf">
          <BrainCircuitIcon className="h-5 w-5 text-[#D1005D] mt-1 flex-shrink-0" data-id="element-19" data-mp="r7vs43g7uwro" />
          <div data-id="element-20" data-mp="n3nypsytcsn4">
            <h3 className="font-medium text-[#003B64] mb-2" data-id="element-21" data-mp="n35xehxano8c">
              Detailed Asset Analysis
            </h3>
            <p className="text-sm text-[#212326] leading-relaxed" data-id="element-22" data-mp="i7qjxr377ucg">
              Each asset has been analysed using visual behavioural data to
              identify areas of high engagement and attention. The data reveals
              how users interact with different elements of each image in a
              natural viewing environment, highlighting focal points that drive
              stopping power. This behavioural approach allows us to understand
              why certain visuals perform better and provides actionable
              insights for your retail displays.
            </p>
          </div>
        </div>
      </div>

      <div data-export-hide className="flex flex-col items-center space-y-4" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={() => setTouchStart(null)} data-id="element-23" data-mp="2tspyokxrhq3z">
        <div className="flex items-center justify-center space-x-4 w-full" data-id="element-24" data-mp="215dcwczfdmbq">
          <button onClick={prevAsset} className="p-2 rounded-sm bg-[#D1005D]/10 hover:bg-[#D1005D]/20 transition-colors shadow-sm" aria-label="Previous asset" data-id="element-25" data-mp="262vokb4tufnf">
            <ChevronLeftIcon className="h-5 w-5 text-[#D1005D]" data-id="element-26" data-mp="3hjd9pf2z4vur" />
          </button>
          {data.map((asset, index) => <motion.div key={asset.id} whileHover={{
          y: -4
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 10
        }} className={`cursor-pointer rounded-sm overflow-hidden bg-[#F9F9FB] ${selectedIndex === index ? 'ring-2 ring-[#D1005D] ring-offset-2' : 'ring-1 ring-gray-200'}`} onClick={() => setSelectedIndex(index)} data-id="element-27" data-mp="3mg3ps2es3rg6" data-mpi={index}>
              <img src={asset.thumbnailUrl} alt={asset.name} className="w-12 h-16 object-cover" referrerPolicy="no-referrer" loading="lazy" data-id="element-28" data-mp="2woapn5vb2r9" />
            </motion.div>)}
          <button onClick={nextAsset} onMouseEnter={() => setIsNextHovered(true)} onMouseLeave={() => setIsNextHovered(false)} className="p-2 rounded-sm bg-[#D1005D]/10 hover:bg-[#D1005D]/20 transition-colors shadow-sm" aria-label="Next asset" data-id="element-29" data-mp="gv4nnvubpphu">
            <ChevronRightIcon className="h-5 w-5 text-[#D1005D]" data-id="element-30" data-mp="3cqr75pcddg0y" />
          </button>
        </div>
        <div className="text-sm text-[#707070]" data-id="element-31" data-mp="37uafil5yojj4">
          {isNextHovered ? <div className="flex items-center" data-id="element-32" data-mp="331hcc1q55j95">
              <span data-id="element-33" data-mp="3306860ssa9hi">Next: </span>
              <span className="text-[#D1005D] font-medium ml-1" data-id="element-34" data-mp="2y3pmdd92477l">
                {getNextAssetName()}
              </span>
            </div> : <div className="flex items-center" data-id="element-35" data-mp="2u2vuy9weoa9y">
              <ImagesIcon className="h-3 w-3 mr-1" data-id="element-36" data-mp="2p1yec1r1noak" />
              <span data-id="element-37" data-mp="2p4kng5tl2buw">View all {data.length} assets</span>
            </div>}
        </div>
      </div>

      <div data-export-hide className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-38" data-mp="2k581roeg7atw">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start" data-id="element-39" data-mp="iaz03ecdckaz">
          {/* Left Column - Video with Comparison Slider */}
          <div className="flex flex-col" data-id="element-40" data-mp="dauitm3mzljp">
            {/* Video Player with Comparison Slider */}
            <div className="flex flex-col" data-id="element-41" data-mp="27r2o9wnfkybd">
              {/* Video Display with Slider */}
              <div ref={videoContainerRef} className="relative rounded-sm overflow-hidden bg-[#F9F9FB] mx-auto select-none w-full" style={{
              maxWidth: '340px',
              aspectRatio: '4 / 5',
              cursor: isDragging ? 'grabbing' : 'grab'
            }} onMouseDown={handleSliderMouseDown} onTouchStart={handleSliderTouchStart} data-id="element-42" data-mp="2cl6xyno2zkg5">
                <div className="absolute inset-0" data-id="element-43" data-mp="2hhxc35gbs5yx">
                  {/* Original Image (Left Side) */}
                  <div className="absolute inset-0" data-id="element-44" data-mp="2ljis2zo5p0dc">
                    {selectedAsset.thumbnailUrl ? <img src={selectedAsset.thumbnailUrl} alt={`${selectedAsset.name} original`} className="w-full h-full object-contain" referrerPolicy="no-referrer" loading="eager" data-id="element-45" data-mp="1630h7exgmmjn" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-46" data-mp="1cjs9h3d1lzb4">
                        No thumbnail
                      </div>}
                    <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-sm text-xs font-medium pointer-events-none z-20" data-id="element-47" data-mp="jvecmqxox915">
                      Original
                    </div>
                  </div>

                  {/* Heatmap Overlay (Right Side) - Clipped */}
                  <div className="absolute inset-0 pointer-events-none" style={{
                  clipPath: `inset(0 0 0 ${sliderPosition}%)`
                }} data-id="element-48" data-mp="tn0sj0kz3wa8">
                    {selectedAsset.heatmapUrl || selectedAsset.thumbnailUrl ? <img src={selectedAsset.heatmapUrl || selectedAsset.thumbnailUrl} alt={`${selectedAsset.name} heatmap`} className="w-full h-full object-contain" referrerPolicy="no-referrer" loading="eager" onError={e => {
                    const img = e.currentTarget;
                    if (selectedAsset.thumbnailUrl && img.src !== selectedAsset.thumbnailUrl) {
                      img.src = selectedAsset.thumbnailUrl;
                    }
                  }} data-id="element-49" data-mp="2vk2xxnjc4988" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-50" data-mp="1sy9i29qrjapp">
                        No heatmap
                      </div>}
                    <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-sm text-xs font-medium z-20" data-id="element-51" data-mp="1qctakxcj1jv4">
                      Attention heatmap
                    </div>
                  </div>

                  {/* Vertical Slider Line */}
                  <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg pointer-events-none z-30" style={{
                  left: `${sliderPosition}%`,
                  transform: 'translateX(-50%)'
                }} data-id="element-52" data-mp="1bnnhlr4a8pvo">
                    {/* Slider Handle */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center pointer-events-auto cursor-grab active:cursor-grabbing" style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }} data-id="element-53" data-mp="1zbww07z62bcs">
                      <div className="flex gap-0.5" data-id="element-54" data-mp="leocn8k6w1dn">
                        <div className="w-0.5 h-3 bg-gray-400 rounded-full" data-id="element-55" data-mp="lbsblbd1owkz"></div>
                        <div className="w-0.5 h-3 bg-gray-400 rounded-full" data-id="element-56" data-mp="qan79dw42fmi"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="mt-4 text-center" data-id="element-57" data-mp="2nf0ssmy50iw">
                <p className="text-xs text-gray-500" data-id="element-58" data-mp="2lkilm327mii">
                  Drag the slider to compare the original creative vs the
                  attention heatmap
                </p>
              </div>

              <div className="mt-3 text-center" data-id="element-59" data-mp="3ow6jpds514d0">
                <h4 className="font-medium text-gray-900" data-id="element-60" data-mp="3tswy7woanrwb">
                  {selectedAsset.name}
                </h4>
              </div>
            </div>
          </div>

          {/* Right Column - AI Insights */}
          <div className="flex flex-col" ref={insightsRef} data-id="element-61" data-mp="3jvim299elp9t">
            {/* AI Insights - Expanded */}
            <div className="flex flex-col" data-id="element-62" data-mp="317zvj56exh6b">
              <div className="flex items-center space-x-2 mb-4" data-id="element-63" data-mp="318t7qlo54yvu">
                <BrainCircuitIcon className="h-5 w-5 text-[#D1005D]" data-id="element-64" data-mp="2w96rs4gxh4jc" />
                <motion.h3 className="font-medium text-[#003B64] flex items-center" initial={{
                x: 20,
                opacity: 0
              }} animate={controls} data-id="element-65" data-mp="3a869es60wj60">
                  Attention Insights
                  <motion.span initial={{
                  opacity: 0,
                  scale: 0
                }} animate={isInView ? {
                  opacity: 1,
                  scale: 1
                } : {}} transition={{
                  delay: 0.3,
                  duration: 0.5
                }} className="flex items-center ml-2 bg-[#D1005D]/10 text-[#D1005D] px-2 py-0.5 rounded-sm text-xs" data-id="element-66" data-mp="3f7j39wvbaqi3">
                    <SparklesIcon className="h-3 w-3 mr-1" data-id="element-67" data-mp="1d7qcabcguh9y" />
                    Powered by AI
                  </motion.span>
                </motion.h3>
              </div>

              {/* Summary */}
              <motion.div className="mb-6" initial={{
              y: 10,
              opacity: 0
            }} animate={isInView ? {
              y: 0,
              opacity: 1
            } : {}} transition={{
              delay: 0.4,
              duration: 0.5
            }} data-id="element-68" data-mp="pgb9khgs1xy6">
                <p className="text-[#003B64] font-semibold mb-2" data-id="element-69" data-mp="bowq69iyjuql">
                  {feedback.summary}
                </p>
                {feedback.details.length === 1 ? <p className="text-[#212326] leading-relaxed" data-id="element-70" data-mp="44m11yihqldh">
                    {feedback.details[0]}
                  </p> : <ul className="list-disc pl-5 space-y-2 text-[#212326] leading-relaxed" data-id="element-71" data-mp="3qbsqkxd3tcbb">
                    {feedback.details.map((detail, idx) => <li key={idx} data-id="element-72" data-mp="3lflhivpevkp5" data-mpi={idx}>{detail}</li>)}
                  </ul>}
              </motion.div>

              {/* Detailed Analysis */}

              {/* Key Metrics */}
              <motion.div className="mt-6 space-y-4" initial={{
              y: 20,
              opacity: 0
            }} animate={isInView ? {
              y: 0,
              opacity: 1
            } : {}} transition={{
              delay: 0.8,
              duration: 0.5
            }} data-id="element-73" data-mp="37m3ydc0n09zn">
                <h4 className="text-sm font-medium text-[#003B64]" data-id="element-74" data-mp="1dk1o6tiahxrn">
                  Performance Metrics
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-75" data-mp="19hf115wkd10s">
                  <div className="bg-[#F9F9FB] border border-[#F2F3F5] p-3 rounded-sm" data-id="element-76" data-mp="19k1c3finxynr">
                    <p className="text-sm text-[#707070]" data-id="element-77" data-mp="14l7vhe82xrjd">SPR Score</p>
                    <p className="text-lg font-medium text-[#D1005D]" data-id="element-78" data-mp="zkshqljvyi0v">
                      {selectedAsset.stoppingPower}
                    </p>
                  </div>
                  <div className="bg-[#F9F9FB] border border-[#F2F3F5] p-3 rounded-sm" data-id="element-79" data-mp="ptfs80lk83y0">
                    <p className="text-sm text-[#707070]" data-id="element-80" data-mp="lt5ner9i7jkq">Audience Signals</p>
                    <p className="text-lg font-medium text-[#D1005D]" data-id="element-81" data-mp="lqsyfu5bw8y6">
                      {selectedAsset.views.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-[#F9F9FB] border border-[#F2F3F5] p-3 rounded-sm" data-id="element-82" data-mp="bv7wtx469aoy">
                    <p className="text-sm text-[#707070]" data-id="element-83" data-mp="2nh9y6mp0ymql">
                      Probability to be Best
                    </p>
                    <p className="text-lg font-medium text-[#D1005D]" data-id="element-84" data-mp="2sipddy4itz12">
                      {getProbability(selectedAsset.id)}%
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Export-only layout: all assets with full-opacity text + both images.
            Rendered only while generating the PDF so every asset and its
            insights appear, unaffected by scroll-based animations. */}
      {exporting && <div className="space-y-6" data-id="element-85" data-mp="2ij899l4x3t77">
          {data.map(asset => {
        const assetFeedback = getAIFeedback(asset);
        return <div key={asset.id} className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-86" data-mp="1m0k8yiuzzyy0">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start" data-id="element-87" data-mp="2tc0aw8qyx0ud">
                  {/* Images: original + heatmap side by side */}
                  <div className="flex flex-col" data-id="element-88" data-mp="332v11o7c4kl3">
                    <div className="grid grid-cols-2 gap-3" data-id="element-89" data-mp="332l9hw6n65da">
                      <div className="relative rounded-sm overflow-hidden bg-[#F9F9FB] border border-[#F2F3F5]" data-id="element-90" data-mp="2y3i4ucukq15u">
                        <img src={asset.thumbnailUrl} alt={`${asset.name} original`} className="w-full h-full object-contain" style={{
                    aspectRatio: '4 / 5'
                  }} referrerPolicy="no-referrer" data-id="element-91" data-mp="2aficx9lj04po" />
                        <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-0.5 rounded-sm text-[10px] font-medium" data-id="element-92" data-mp="3ut98b1kueyns">
                          Original
                        </div>
                      </div>
                      <div className="relative rounded-sm overflow-hidden bg-[#F9F9FB] border border-[#F2F3F5]" data-id="element-93" data-mp="2598c46vb2x4">
                        <img src={asset.heatmapUrl || asset.thumbnailUrl} alt={`${asset.name} attention heatmap`} className="w-full h-full object-contain" style={{
                    aspectRatio: '4 / 5'
                  }} referrerPolicy="no-referrer" data-id="element-94" data-mp="73u5fuqtp6hc" />
                        <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-0.5 rounded-sm text-[10px] font-medium" data-id="element-95" data-mp="b7s5q8rvwnbz">
                          Attention heatmap
                        </div>
                      </div>
                    </div>
                    <h4 className="mt-3 text-center font-medium text-gray-900" data-id="element-96" data-mp="13uv3uzsnew1p">
                      {asset.name}
                    </h4>
                  </div>

                  {/* Insights + metrics (static, full opacity) */}
                  <div className="flex flex-col" data-id="element-97" data-mp="3c4vv3k3zk6ov">
                    <div className="flex items-center space-x-2 mb-4" data-id="element-98" data-mp="3c42s8u85e1c0">
                      <BrainCircuitIcon className="h-5 w-5 text-[#D1005D]" data-id="element-99" data-mp="374zpxhjjy97q" />
                      <h3 className="font-medium text-[#003B64]" data-id="element-100" data-mp="326y6uwm4m9w9">
                        Attention Insights
                      </h3>
                    </div>
                    <p className="text-[#003B64] font-semibold mb-2" data-id="element-101" data-mp="3oaryjy1c7to">
                      {assetFeedback.summary}
                    </p>
                    {assetFeedback.details.length === 1 ? <p className="text-[#212326] leading-relaxed" data-id="element-102" data-mp="3q2u8onahfz3b">
                        {assetFeedback.details[0]}
                      </p> : <ul className="list-disc pl-5 space-y-2 text-[#212326] leading-relaxed" data-id="element-103" data-mp="mglo7uy6p7it">
                        {assetFeedback.details.map((detail, idx) => <li key={idx} data-id="element-104" data-mp="pxrbm5meaj8c" data-mpi={idx}>{detail}</li>)}
                      </ul>}
                    <h4 className="mt-6 mb-3 text-sm font-medium text-[#003B64]" data-id="element-105" data-mp="u04emm9rc31f">
                      Performance Metrics
                    </h4>
                    <div className="grid grid-cols-3 gap-3" data-id="element-106" data-mp="z0iom9ux5n23">
                      <div className="bg-[#F9F9FB] border border-[#F2F3F5] p-3 rounded-sm" data-id="element-107" data-mp="13y22v9rm9zu1">
                        <p className="text-xs text-[#707070]" data-id="element-108" data-mp="18vkej7x0qt5q">SPR Score</p>
                        <p className="text-lg font-medium text-[#D1005D]" data-id="element-109" data-mp="1cxfby1btwme8">
                          {asset.stoppingPower}
                        </p>
                      </div>
                      <div className="bg-[#F9F9FB] border border-[#F2F3F5] p-3 rounded-sm" data-id="element-110" data-mp="1mnqsqogm2q2j">
                        <p className="text-xs text-[#707070]" data-id="element-111" data-mp="1rmtp7kiiznyw">
                          Audience Signals
                        </p>
                        <p className="text-lg font-medium text-[#D1005D]" data-id="element-112" data-mp="1wl5100sds8jr">
                          {asset.views.toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-[#F9F9FB] border border-[#F2F3F5] p-3 rounded-sm" data-id="element-113" data-mp="11z7se5xv3007">
                        <p className="text-xs text-[#707070]" data-id="element-114" data-mp="1gnc42pyhyb9h">
                          Probability to be Best
                        </p>
                        <p className="text-lg font-medium text-[#D1005D]" data-id="element-115" data-mp="1bw3untx62pwb">
                          {getProbability(asset.id)}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>;
      })}
        </div>}
    </div>;
};